import * as React from 'react';
import * as uniqid from 'uniqid';
import {
  CheckBox,
  Phone,
  Select,
  TextArea,
  TextBox,
  CheckBoxToggle,
  Radio
} from './components';
import { IFormField } from '../../../interfaces/interfaces';
import { Title } from '../../../modules/modules';
import { addressFields, addressTwoLinesFields } from '../../../formFields';

import { CurrencyFormatter as Currency } from '../../../utils/utils';
import { isTwoLineAddressMode } from '../../../helpers/address/isTwoLineAddressMode';
import { getDeliveryCountriesOptions } from '../../../helpers/address/getDeliveryCountriesOptions';
import Image from '../../Image/Image';

import GetCountriesQuery from '../../Query/Countries/Countries';

interface IFormProps {
  fields: IFormField[];
  values: any[];
  placeholders?: any;
  children?: any;
  type?: string;
  formName?: string;
  searchable?: boolean; // for select element
  renderElement?: (formField: IFormField, element: HTMLElement) => void;
  handleChange?: (value: any, field: string, formName?: string) => void;
}

interface IFormState {
  countries?: GQL.ICountry[];
  countryRefetch: () => void;
}

export default class Form extends React.Component<IFormProps, IFormState> {

  public validate = async (updateFields: any) => {
    let formIsValid = true;

    for (let fieldPosition = 0; fieldPosition < this.props.fields.length; fieldPosition++) {

      const field = this.props.fields[fieldPosition];
      // reset error before validation starts
      delete this.props.fields[fieldPosition].errorMessage;
      formIsValid = await this.validateField(field, fieldPosition, formIsValid);
      if (!field.errorMessage && typeof field.validation === 'function') {
        const v = await field.validation(this.props.values[field.fieldName], this.props.values, this.props.fields[fieldPosition]);
        if (v === false) {
          field.errorMessage = field.validationMessage || 'Invalid value';
          formIsValid = false;
        }
      }
    }

    updateFields(this.props.fields);
    return formIsValid;
  }

  public async onSubmit(e: any) {
    e.preventDefault();
    return false;
  }

  public async validateField(
    field: IFormField,
    fieldPosition: number,
    formIsValid: boolean,
    subFieldPosition?: number
  ) {

    // Clear errors
    field.errorMessage = '';
    // Custom rulesp
    if (field.validation && field.validationMessage) {
      // clear previous error
      field.errorMessage = '';
      // check if value is valid
      const isValid = await field.validation(this.props.values[field.fieldName], this.props.values, field);
      if (!isValid) {
        field.errorMessage = field.validationMessage;
        return false;
      }
    }

    // Equal to..
    if (field.equal) {
      const equalField = this.props.fields.find(f => f.fieldName === field.equal);
      if (equalField) {
        if (this.props.values[field.fieldName] !== this.props.values[field.equal]) {
          const label = this.props.fields;
          this.writeErrorMessage(field, `${field.label} is not equal to ${equalField.label}`);
          formIsValid = false;
        }
      }
    }

    // Is required

    switch (field.type) {
      case 'bool':

        if (
          (typeof field.isRequired === 'function'
            ? field.isRequired()
            : field.isRequired) &&
          this.props.values[field.fieldName] !== true
        ) {
          this.writeErrorMessage(field);
          formIsValid = false;
        }
        break;

      case 'select':
      case 'password':
      case 'textarea':
      case 'text':
        if (
          (typeof field.isRequired === 'function'
            ? field.isRequired()
            : field.isRequired) &&
          (this.props.values[field.fieldName]
            ? this.props.values[field.fieldName].length === 0
            : true)
        ) {
          this.writeErrorMessage(field);
          formIsValid = false;
        }
        break;

      case 'email':

        let isValid = true;

        if (
          typeof field.isRequired === 'function'
            ? field.isRequired()
            : field.isRequired
        ) {

          // Is set?
          if (
            !this.props.values[field.fieldName] ||
            (this.props.values[field.fieldName] &&
              this.props.values[field.fieldName].length === 0)
          ) {
            this.writeErrorMessage(field);
            isValid = false;
          }

          // Is email valid?
          if (isValid) {
            const isEmail = /\S+@\S+\.\S+/.test(this.props.values[field.fieldName]);

            if (!isEmail) {
              this.writeErrorMessage(field, 'E-mail address is not in correct format');
              isValid = false;
            }
          }

          if (!isValid) {
            formIsValid = false;
          }

        }
        break;

      case 'phone':
        if (
          typeof field.isRequired === 'function'
            ? field.isRequired()
            : field.isRequired
        ) {
          const prefixFieldName =
            'prefix' +
            field.fieldName.charAt(0).toUpperCase() +
            field.fieldName.slice(1);
          // prefix phone number
          if (
            !this.props.values[prefixFieldName] ||
            (this.props.values[prefixFieldName] &&
              this.props.values[prefixFieldName].length === 0)
          ) {
            this.writeErrorMessage(field);
            formIsValid = false;
          }
          // phone number
          if (formIsValid) {
            if (
              !this.props.values[field.fieldName] ||
              (this.props.values[field.fieldName] &&
                this.props.values[field.fieldName].length === 0)
            ) {
              this.writeErrorMessage(field);
              formIsValid = false;
            }
          }
        }
        break;

      case 'hidden':
      case 'address':
        const shouldIValidateSubFields = this.props.values[field.fieldName] || field.type === 'address';
        if (field.fields && shouldIValidateSubFields) {
          for (const item of field.fields) {
            formIsValid = await this.validateField(item, fieldPosition, formIsValid);
          }
        }
        break;

      case 'radioTable':
        if (field.isRequired) {
          if (this.props.values[field.fieldName] === undefined) {
            this.writeErrorMessage(field);
            formIsValid = false;
          }
        }
        break;

      case 'title':
        break;

      default:
        console.warn(field.fieldName, ' Unknown field type');
        break;
    }
    return formIsValid;
  }

  public writeErrorMessage(
    field: IFormField,
    message?: string
  ) {
    const props = this.props;

    // todo: překlady
    let msg: string = `${field.label} ` + 'is required!';

    if (message) {
      msg = message;
    } else if (field.validationMessage) {
      msg = field.validationMessage;
    }

    field.errorMessage = msg;
  }

  public handleField(field: any) {
    if (field.type === 'title' || field.type === 'address') { return; }

    field.onChange = (e: any) => {
      if (this.props.handleChange && e) {

        let value = e.target ? e.target.value : e.value;
        if (field.type === 'bool' || field.type === 'hidden') {
          value = e.target.checked;
        }

        this.props.handleChange(value, field.fieldName, this.props.formName);
      }
    };

    if (field.fields) {
      field.fields.map((subField: any) => {
        this.handleField(subField);
      });
    }
  }

  public renderField(formField: IFormField): any {
    if (formField === undefined) {
      return null;
    }

    this.handleField(formField);
    // formField.onChange = (e: any) => {
    //   if (this.props.handleField) {
    //     this.props.handleField(e.target.value, formField.fieldName);
    //   }
    // };

    switch (formField.type) {
      case 'bool':
        return (
          <CheckBox
            key={formField.fieldName}
            {...formField}
            checked={this.props.values[formField.fieldName]}
          />
        );

      case 'password':
      case 'text':
      case 'email':
        return (
          <>
            <TextBox
              disabled={formField.disabled}
              key={formField.fieldName}
              {...formField}
              value={this.props.values[formField.fieldName]}
              values={this.props.values}
              placeholder={formField.placeholder || this.props.placeholders && this.props.placeholders[formField.fieldName]}
            />
          </>
        );

      case 'select':
        if (formField.options && !this.props.values[formField.fieldName]) {
          if (formField.options && formField.options.length > 0) {
            this.props.values[formField.fieldName] = formField.options[0].value;
          }
        }

        return (
          <Select
            key={formField.fieldName}
            disabled={formField.disabled}
            value={this.props.values[formField.fieldName]}
            values={this.props.values}
            placeholder={formField.placeholder || this.props.placeholders && this.props.placeholders[formField.fieldName]}
            searchable={formField.searchable || false}
            {...formField}
          />
        );

      case 'textarea':
        return (
          <TextArea
            key={formField.fieldName}
            {...formField}
            value={this.props.values[formField.fieldName]}
          />
        );

      case 'phone':
        return (
          <Phone
            key={formField.fieldName}
            {...formField}
            value={this.props.values[formField.fieldName]}
            valuePrefix={
              this.props.values[
              'prefix' +
              formField.fieldName.charAt(0).toUpperCase() +
              formField.fieldName.slice(1)
              ]
            }
          />
        );

      case 'hidden':
        return (
          <CheckBoxToggle
            key={formField.fieldName}
            {...formField}
            label={formField.label}
            values={this.props.values}
            checked={formField.checked || this.props.values[formField.fieldName] === true}
          >
            {formField.fields &&
              formField.fields.map(field => {
                return this.renderField(field);
              })}
          </CheckBoxToggle>
        );

      case 'radioTable':
        // ma seznam obrazky ?
        const hasImages = formField.fields &&
          formField.fields.reduce((acc, curr) => curr.image ? true : acc, false);

        // vybraná hodnota
        const selectedValue = this.props.values[formField.fieldName];

        return (
          <div key={formField.fieldName}>
            <table className="table table--form">
              <tbody>
                {formField.fields &&
                  formField.fields.map((item: any, i: number) => {
                    item.fieldName = formField.fieldName;

                    const label =
                      <span>
                        {hasImages &&
                          <div className="image">
                            <Image image={item.image} />
                          </div>}
                        {item.label}
                      </span>;

                    return (
                      <tr key={i} className={(selectedValue === item.id ? 'active' : '')}>
                        <td className="table__radio">
                          <Radio
                            fieldName={formField.fieldName}
                            value={item.id}
                            checked={selectedValue === item.id}
                            label={label}
                            onChange={item.onChange}
                          />
                        </td>
                        <td
                          className="table__description"
                          dangerouslySetInnerHTML={{ __html: ('' + item.description).replace(/\n/g, '<br>') }}
                        />
                        <td className="table__price">
                          <Currency price={item.price} />
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
            {formField.errorMessage && (
              <p className={'form__error form__error--static'}>
                {formField.errorMessage}
              </p>
            )}
          </div>
        );

      case 'address':
        if (!formField.inited) {

          const sourceFields = formField.fields
            ? formField.fields
            : addressTwoLinesFields;

          const fields = sourceFields.map((sourceField: any) => {
            const field = JSON.parse(JSON.stringify(sourceField));
            field.fieldName = formField.fieldName + field.fieldName;

            if (field.isRequired === undefined && formField.isRequired !== undefined) {
              field.isRequired = formField.isRequired;
            }
            field.disabled = sourceField.disabled;

            return field;
          });
          
          formField.fields = fields;
          formField.inited = true;
        }

        if (formField.fields) {
          if (isTwoLineAddressMode() && this.state && this.state.countries) {
            const addresCountry = formField.fieldName === 'DeliveryAddress'
              ? (this.props.values as any).DeliveryAddressCountry
              : (this.props.values as any).AddressCountry;
            const country = this.state.countries.find((c) => c.name === addresCountry);
            formField.fields[4].type = country && country.states && (country.states.length > 0) ? 'select' : 'text';
            if (country && country.states && (country.states.length > 0)) {
              formField.fields[4].options = country.states.map((s) => ({ value: s.name, label: s.name }));
              // fixes after switching from text to select to have consistent
              // data with what user sees
              if (formField.fieldName === 'Address') {
                const currentState = country.states.find((s) => s.name === (this.props.values as any).AddressCountyState);
                if (!currentState) {
                  if (this.props.handleChange) {
                    this.props.handleChange(country.states[0].name, 'AddressCountyState', this.props.formName);
                  }
                }
              }
              if (formField.fieldName === 'DeliveryAddress') {
                const currentState = country.states.find((s) => s.name === (this.props.values as any).AddressCountyState);
                if (!currentState) {
                  if (this.props.handleChange) {
                    this.props.handleChange(country.states[0].name, 'DeliveryAddressCountyState', this.props.formName);
                  }
                }
              }
            }
            // Po přihlášení se stane, že načtené countries mají chybně .states = null
            // pak je potřeba udělat refetch
            if (country && country.states === null) {
              this.setState({ countries: undefined }, () => this.state.countryRefetch());
            }
          }

          return (
            <React.Fragment key={formField.fieldName}>
              {isTwoLineAddressMode()
                ? <React.Fragment>
                  {/** DeliveryAddressPhone is not displayed when invoice address appears */}
                  {formField.fieldName === 'DeliveryAddress' ? this.renderField(formField.fields[6]) : <></>}
                  {this.renderField(formField.fields[0])}
                  {this.renderField(formField.fields[1])}
                  <div className="form__doubleItem">
                    {this.renderField(formField.fields[2])}
                    {this.renderField(formField.fields[3])}
                  </div>
                  {this.renderField(formField.fields[4])}
                </React.Fragment>
                : <React.Fragment>
                  <div className="form__doubleItem">
                    {this.renderField(formField.fields[0])}
                    {this.renderField(formField.fields[1])}
                  </div>
                  <div className="form__doubleItem">
                    {this.renderField(formField.fields[2])}
                    {this.renderField(formField.fields[3])}
                  </div>
                </React.Fragment>}

              <GetCountriesQuery
                withoutContinents={true}
              >
                {({ countries }: any) => {
                  const countryField = formField && formField.fields && formField.fields[isTwoLineAddressMode() ? 5 : 4]; // FUJ !!!!
                  if (!countries.loading && countries.countries) {
                    if (!this.state || !Array.isArray(this.state.countries)) {
                      this.setState({
                        countries: countries.countries,
                        countryRefetch: countries.refetch,
                      });
                    }

                    if (countryField.fieldName === 'DeliveryAddressCountry') {
                      const invoiceCountry = countries.countries.find(
                        /* tslint:disable:no-string-literal */
                        (c: GQL.ICountry) =>
                          c.name === this.props.values['AddressCountry'],
                      );

                      countryField.options = getDeliveryCountriesOptions(countries.countries, invoiceCountry);
                    } else {
                      countryField.options = countries.countries
                        .filter((country: GQL.ICountry) => country.code !== null)
                        .map((country: GQL.ICountry) => {
                          return {
                            value: country.name,
                            label: country.name,
                          };
                        });
                    }

                    countryField.disabled = countryField.disabled || false;
                    countryField.searchable = true;
                  }
                  return this.renderField(countryField);
                }}
              </GetCountriesQuery>
            </React.Fragment>
          );

        }

        break;

      case 'title':
        return (
          <Title type="form" key={uniqid()}>
            {formField.label}
          </Title>
        );

      default:
        return (
          <TextBox
            key={formField.fieldName}
            {...formField}
            value={this.props.values[formField.fieldName]}
          />
        );
    }
  }

  public render() {
    const childProps = {
      validate: this.validate
    };

    return (
      <form method="post" onSubmit={(e: any) => this.onSubmit(e)}>
        {this.props.fields.map(
          (formField: IFormField, fieldPosition: number) => {
            const element = this.renderField(formField);
            if (this.props.renderElement) {
              return this.props.renderElement(formField, element);
            } else {
              return element;
            }
          }
        )}
        {this.props.children &&
          this.props.children(childProps)}
      </form>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/FormGenerator/Form/Form.tsx